import React, { ReactNode, CSSProperties } from 'react';

interface ModalProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}

export function Modal({ title, isOpen, onClose, children, wide }: ModalProps) {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--color-background-primary)',
          borderRadius: 'var(--border-radius-lg)',
          padding: '1.25rem',
          width: '100%',
          maxWidth: wide ? 600 : 420,
          maxHeight: '88vh',
          overflowY: 'auto',
          border: '0.5px solid var(--color-border-secondary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 18,
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: ReactNode;
  required?: boolean;
}

export function Field({ label, children, required }: FieldProps) {
  return (
    <div style={{ marginBottom: '0.875rem' }}>
      <label
        style={{
          display: 'block',
          fontSize: 12,
          color: 'var(--color-text-secondary)',
          marginBottom: 4,
          fontWeight: required ? 500 : 'normal',
        }}
      >
        {label}
        {required && ' *'}
      </label>
      {children}
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string | number;
  color?: string;
  sub?: string;
  onClick?: () => void;
}

export function StatCard({ label, value, color, sub, onClick }: StatCardProps) {
  return (
    <div
      style={{
        background: 'var(--color-background-secondary)',
        borderRadius: 'var(--border-radius-md)',
        padding: '0.875rem 1rem',
        cursor: onClick ? 'pointer' : 'default',
        transition: onClick ? 'background 0.2s' : undefined,
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (onClick) e.currentTarget.style.opacity = '0.8';
      }}
      onMouseLeave={(e) => {
        if (onClick) e.currentTarget.style.opacity = '1';
      }}
    >
      <p style={{ margin: '0 0 4px', fontSize: 12, color: 'var(--color-text-secondary)' }}>{label}</p>
      <p style={{ margin: 0, fontSize: 22, fontWeight: 500, color: color || 'var(--color-text-primary)' }}>
        {value}
      </p>
      {sub && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--color-text-tertiary)' }}>{sub}</p>}
    </div>
  );
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
}

export function Button({ variant = 'primary', size = 'md', children, ...props }: ButtonProps) {
  const baseStyle: CSSProperties = {
    border: 'none',
    borderRadius: 'var(--border-radius-md)',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'all 0.2s',
  };

  const variants = {
    primary: {
      backgroundColor: 'var(--color-accent)',
      color: '#ffffff',
    },
    secondary: {
      backgroundColor: 'var(--color-background-secondary)',
      color: 'var(--color-text-primary)',
      border: '0.5px solid var(--color-border-secondary)',
    },
    danger: {
      backgroundColor: 'var(--color-danger)',
      color: '#ffffff',
    },
  };

  const sizes = {
    sm: { padding: '4px 8px', fontSize: 11 },
    md: { padding: '6px 12px', fontSize: 13 },
    lg: { padding: '8px 16px', fontSize: 14 },
  };

  return (
    <button style={{ ...baseStyle, ...variants[variant], ...sizes[size] }} {...props}>
      {children}
    </button>
  );
}

interface CardProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export function Card({ children, className, onClick, style }: CardProps) {
  return (
    <div
      style={{
        background: 'var(--color-background-secondary)',
        borderRadius: 'var(--border-radius-md)',
        padding: '1rem',
        border: '0.5px solid var(--color-border-secondary)',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
      onClick={onClick}
      className={className}
    >
      {children}
    </div>
  );
}

interface LoadingProps {
  message?: string;
}

export function Loading({ message = 'Chargement...' }: LoadingProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        color: 'var(--color-text-secondary)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 24,
            height: 24,
            border: '2px solid var(--color-border-secondary)',
            borderTop: '2px solid var(--color-accent)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 0.5rem',
          }}
        />
        <p style={{ margin: 0, fontSize: 13 }}>{message}</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

interface EmptyStateProps {
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem 1rem',
        textAlign: 'center',
        color: 'var(--color-text-secondary)',
      }}
    >
      <h3 style={{ margin: '0 0 0.5rem', fontSize: 16, color: 'var(--color-text-primary)' }}>{title}</h3>
      {description && <p style={{ margin: '0 0 1rem', fontSize: 13 }}>{description}</p>}
      {action && (
        <Button onClick={action.onClick} size="sm">
          {action.label}
        </Button>
      )}
    </div>
  );
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

export function Input({ ...props }: InputProps) {
  return (
    <input
      style={{
        width: '100%',
        padding: '0.5rem',
        borderRadius: 'var(--border-radius-md)',
        border: '0.5px solid var(--color-border-secondary)',
        backgroundColor: 'var(--color-background-primary)',
        color: 'var(--color-text-primary)',
        fontSize: 13,
        fontFamily: 'inherit',
        boxSizing: 'border-box',
      }}
      {...props}
    />
  );
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  children: ReactNode;
  style?: React.CSSProperties;
}

export function Select({ children, style, ...props }: SelectProps) {
  return (
    <select
      style={{
        width: '100%',
        padding: '0.5rem',
        borderRadius: 'var(--border-radius-md)',
        border: '0.5px solid var(--color-border-secondary)',
        backgroundColor: 'var(--color-background-primary)',
        color: 'var(--color-text-primary)',
        fontSize: 13,
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        cursor: 'pointer',
        ...style,
      }}
      {...props}
    >
      {children}
    </select>
  );
}
